'use client'

import { cva } from 'class-variance-authority'
import React from 'react'

import { cn } from '../../utils/cn'

const tabVariants = cva(
  'flex min-w-px flex-1 items-center justify-center border-0 border-b border-solid bg-transparent py-4 typography-button1',
  {
    variants: {
      active: {
        true: 'cursor-default border-blue-01 text-blue-01',
        false: 'cursor-pointer border-gray-04 text-gray-03',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
)

export interface TabsProps<T extends string> {
  /**
   * Class name
   */
  className?: string

  /**
   * Base id used to associate each tab with its panel.
   * Falls back to a generated id when omitted.
   */
  id?: string

  /**
   * Accessible label for the tablist
   */
  'aria-label'?: string

  /**
   * Tabs
   */
  tabs: {
    label: string
    value: T
  }[]

  /**
   * Active tab
   */
  value: T

  /**
   * onChange handler
   */
  onChange: (value: T) => void
}

/**
 * Tabs component
 */
export const Tabs = <T extends string>({
  className,
  id,
  'aria-label': ariaLabel,
  tabs,
  value,
  onChange,
}: TabsProps<T>) => {
  const generatedId = React.useId()
  const baseId = id ?? generatedId
  const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([])

  // Automatic activation: moving focus also selects the tab
  const activate = (index: number) => {
    const tab = tabs[index]

    if (!tab) return

    tabRefs.current[index]?.focus()

    if (tab.value !== value) onChange(tab.value)
  }

  const onKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault()
        activate((index + 1) % tabs.length)
        break
      case 'ArrowLeft':
        event.preventDefault()
        activate((index - 1 + tabs.length) % tabs.length)
        break
      case 'Home':
        event.preventDefault()
        activate(0)
        break
      case 'End':
        event.preventDefault()
        activate(tabs.length - 1)
        break
      default:
        break
    }
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn('flex w-full items-start justify-center', className)}
    >
      {tabs.map((tab, index) => {
        const active = tab.value === value

        return (
          <button
            key={tab.value}
            ref={(node) => {
              tabRefs.current[index] = node
            }}
            type="button"
            role="tab"
            id={`${baseId}-tab-${tab.value}`}
            aria-controls={`${baseId}-panel-${tab.value}`}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            className={tabVariants({ active })}
            onClick={() => {
              if (!active) onChange(tab.value)
            }}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
